import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import SafeheirLogo from '../components/SafeheirLogo';

const COOLDOWN_SECONDS = 60;
const SUCCESS_DISPLAY_SECONDS = 5;

const MailIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const [resendState, setResendState] = useState('idle');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    if (!email) {
      navigate('/register');
    }
  }, [email, navigate]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCooldown = () => {
    setResendState('cooldown');
    setCooldownSeconds(COOLDOWN_SECONDS);
    timerRef.current = setInterval(() => {
      setCooldownSeconds(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setResendState('idle');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (!email || resendState === 'sending' || resendState === 'cooldown') return;
    setResendState('sending');
    setErrorMsg('');
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      if (error) {
        setErrorMsg(error.message || 'Une erreur est survenue, veuillez réessayer');
        setResendState('error');
        return;
      }
      setResendState('success');
      setTimeout(() => {
        startCooldown();
      }, SUCCESS_DISPLAY_SECONDS * 1000);
    } catch {
      setErrorMsg('Une erreur est survenue, veuillez réessayer');
      setResendState('error');
    }
  };

  const getButtonLabel = () => {
    if (resendState === 'sending') return 'Envoi en cours...';
    if (resendState === 'success') return 'Email renvoyé';
    if (resendState === 'cooldown') return `Renvoi possible dans ${cooldownSeconds}s`;
    return 'Renvoyer l\'email';
  };

  const isButtonDisabled = resendState === 'sending' || resendState === 'success' || resendState === 'cooldown';

  const buttonColor = resendState === 'success' ? '#00C77A' : resendState === 'cooldown' ? 'rgba(0,245,160,0.35)' : '#00F5A0';

  if (!email) return null;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0A1628',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <SafeheirLogo size="lg" />
          </Link>
        </div>

        <div style={{
          background: 'rgba(10,22,40,0.7)',
          backdropFilter: 'blur(20px)',
          borderRadius: 16,
          padding: '2.5rem 2rem',
          border: '1px solid rgba(255,255,255,0.07)',
          textAlign: 'center',
        }}>
          <div style={{
            width: 64,
            height: 64,
            background: 'rgba(0,245,160,0.08)',
            border: '1px solid rgba(0,245,160,0.2)',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            color: '#00F5A0',
          }}>
            <MailIcon />
          </div>

          <h1 style={{ margin: '0 0 0.75rem', fontSize: '1.4rem', fontWeight: 700, color: '#fff' }}>
            Vérifiez votre email
          </h1>

          <p style={{ margin: '0 0 0.5rem', color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', lineHeight: 1.6 }}>
            Nous avons envoyé un lien de confirmation à
          </p>
          <p style={{ margin: '0 0 2rem', color: '#00F5A0', fontSize: '0.95rem', fontWeight: 600, wordBreak: 'break-all' }}>
            {email}
          </p>
          <p style={{ margin: '0 0 2rem', color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', lineHeight: 1.6 }}>
            Cliquez sur le lien dans cet email pour activer votre compte. Pensez à vérifier vos courriers indésirables.
          </p>

          {resendState === 'error' && errorMsg && (
            <div style={{
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 16,
              color: '#f87171',
              fontSize: 13,
              textAlign: 'left',
            }}>
              {errorMsg}
            </div>
          )}

          <button
            onClick={handleResend}
            disabled={isButtonDisabled}
            style={{
              width: '100%',
              padding: '11px 0',
              background: buttonColor,
              color: '#0A1628',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 14,
              cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {getButtonLabel()}
          </button>
        </div>

        <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
          <Link to="/" style={{ color: 'rgba(255,255,255,0.35)', textDecoration: 'none', fontSize: '0.875rem' }}>
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
